import React, { useState, useEffect } from "react";
import { ShopInvoiceLogs } from "pages/Logs/MarketplaceLogs/ShopInvoice";
import { Tabs, TabList, Tab, TabPanel } from "react-tabs";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  MarketplaceConfig,
  Marketplace,
  PullAccountsInput,
  RestartInvoicesInput,
} from "TypesAndInterfaces";
import { getMarketplaceQuery } from "pages/Marketplaces/queries";
import {
  clearAll,
  displayError,
  displaySuccess,
} from "utils/displayNotification";
import { initialState } from "utils/validation";
import ControlSyncs from "./components/ControlSyncs";
import { useSearchParams } from "react-router-dom";
import MarketplaceUsers from "pages/Users/MarketplaceUsers";
import {
  restartInvoicesMutation,
  retryInvoicesMutation,
  syncShopAccountsMutation,
} from "./graphql";
import Reports from "pages/Reports/Reports";
import FirstMarketplace from "pages/FirstMarketplace/FirstMarketplace";

const MarketplaceControl = () => {
  const [searchParams] = useSearchParams();
  const [marketplaceConfig, setMarketplaceConfig] =
    useState<MarketplaceConfig>(initialState);
  const [marketplaceId, setMarketplaceId] = useState<number>();
  const [disabled, setDisabled] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const [getMarketplace] = useLazyQuery<
    { getMarketplace: Marketplace },
    { id: number }
  >(getMarketplaceQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplace) => {
      const { config } = dataMarketplace.getMarketplace;
      setMarketplaceConfig(config);
    },
    onError: (e) => {
      displayError(e.message);
    },
  });

  const [startShopSync] = useMutation<PullAccountsInput>(
    syncShopAccountsMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Sync succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  const [restartInvoices] = useMutation<RestartInvoicesInput>(
    restartInvoicesMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Restart succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  const [retryInvoices] = useMutation<RestartInvoicesInput>(
    retryInvoicesMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Retry succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  useEffect(() => {
    const id = localStorage.getItem("marketplaceId");
    if (id && !isNaN(+id)) {
      const idNumber = Number(id);
      setMarketplaceId(idNumber);
      getMarketplace({ variables: { id: idNumber } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    clearAll();
  }, [tabIndex]);

  const handleShopSync = (ids: string) => {
    const shopIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && shopIds.length) {
      setDisabled(true);
      startShopSync({
        variables: { params: { marketplaceId, shopIds } },
      });
    }
  };

  const handleRestartInvoices = (ids: string) => {
    const invoiceIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && invoiceIds.length) {
      setDisabled(true);
      restartInvoices({
        variables: { params: { marketplaceId, invoiceIds } },
      });
    }
  };

  const handleRetryInvoices = (ids: string) => {
    const invoiceIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && invoiceIds.length) {
      setDisabled(true);
      retryInvoices({
        variables: { params: { marketplaceId, invoiceIds } },
      });
    }
  };

  return (
    <Tabs
      forceRenderTabPanel
      defaultIndex={tabIndex}
      onSelect={(index) => setTabIndex(index)}
      selectedIndex={tabIndex}
    >
      <TabList className="tabs__list">
        <Tab>Activity</Tab>
        <Tab>Logs</Tab>
        <Tab>Users</Tab>
        <Tab>Reports</Tab>
        {/* {isAuthorized && <Tab >Configuration</Tab>} */}
      </TabList>
      <TabPanel>
        <ControlSyncs
          disabled={disabled}
          config={marketplaceConfig.account}
          handleShopSync={handleShopSync}
          handleRetryInvoices={handleRetryInvoices}
          handleRestartInvoices={handleRestartInvoices}
        />
      </TabPanel>
      <TabPanel>
        <ShopInvoiceLogs />
      </TabPanel>
      <TabPanel>
        {marketplaceId && <MarketplaceUsers _marketplaceId={marketplaceId} />}
      </TabPanel>
      <TabPanel>
        <Reports></Reports>
      </TabPanel>
      <TabPanel>
        <FirstMarketplace />
      </TabPanel>
    </Tabs>
  );
};
export default MarketplaceControl;
